import {
  Button,
  DetailList,
  formatDate,
  formatDateTime,
  formatPhone,
  isoDay,
  ListView,
  PageError,
  PageLoading,
  PasswordField,
  pickerDay,
  RecordDrawer,
  TextButton,
  useCursorList,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { DatePicker } from "@majitap/ui/arc/date-picker/date-picker";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { type DropdownItem, DropdownMenu } from "@majitap/ui/arc/dropdown-menu/dropdown-menu";
import { Input } from "@majitap/ui/arc/input/input";
import { Select } from "@majitap/ui/arc/select/select";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { api, errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import {
  type Driver,
  type DriverDetails,
  driverActionsFor,
  fleetActions,
  ownerQuery,
  useRefreshFleet,
} from "../../fleet";
import { useFormCheck } from "../../form-check";
import { APPROVAL_STATUS, type ApprovalStatus } from "../../labels";
import { walletActions } from "../../wallets";
import { ConfirmDialog } from "../shared/confirm";
import { OwnerFilter, SearchBox, useSearchText } from "./filters";
import styles from "./fleet.module.css";
import { OpenShiftDialog } from "./open-shift";
import { type OwnerChoice, OwnerSelect, ownerName } from "./owner-select";
import type { FleetCan, FleetSearch } from "./page";

const APPROVALS = Object.keys(APPROVAL_STATUS) as ApprovalStatus[];
/** A licence's expiry is a calendar day: shown as written, not moved into a time zone. */
const asDay = (day: string) => new Date(`${day}T00:00:00`);

type Dialogs = { kind: "edit" | "approve" | "suspend" | "details" | "shift" | "pin"; driver: Driver } | null;

/** Drivers, newest first, by approval status (`Console / Fleet / Drivers` in the design). */
export function DriversView({
  search,
  set,
  can,
  adding,
  onAdded,
}: {
  search: FleetSearch;
  set: (patch: Partial<FleetSearch>, replace?: boolean) => void;
  can: FleetCan;
  adding: boolean;
  onAdded: () => void;
}) {
  const format = useCompanyFormat();
  const today = isoDay(format.timezone);
  const [dialog, setDialog] = useState<Dialogs>(null);
  const text = useSearchText(search.q, set);
  const list = useCursorList({
    queryKey: ["fleet", "drivers", "list", search.approval ?? "", search.q ?? "", search.owner ?? ""],
    fetchPage: async (cursor) => {
      const { data, error } = await api.GET("/v1/admin/fleet/drivers", {
        params: {
          query: { approval_status: search.approval, q: search.q, ...ownerQuery(search.owner), cursor, limit: 25 },
        },
      });
      if (error || !data) throw errorOf(error);
      return data;
    },
  });
  const close = () => setDialog(null);

  return (
    <>
      <ListView
        caption="Drivers"
        itemName={{ one: "driver", other: "drivers" }}
        rows={list.rows}
        rowKey={(d) => d.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={Boolean(search.approval || search.q || search.owner)}
        onResetFilters={() => {
          text.clear();
          set({ approval: undefined, q: undefined, owner: undefined });
        }}
        empty={{ title: "No drivers yet", description: "Add a driver by phone number, then approve them." }}
        toolbar={
          <div className={styles.toolbarEnd}>
            <SearchBox label="Search drivers" placeholder="Name or phone" text={text} />
            <Select
              label="Approval"
              value={search.approval ?? "any"}
              onValueChange={(v) => set({ approval: v === "any" ? undefined : (v as ApprovalStatus) })}
              options={[
                { value: "any", label: "Any" },
                ...APPROVALS.map((s) => ({ value: s, label: APPROVAL_STATUS[s][0] })),
              ]}
            />
            <OwnerFilter value={search.owner} onChange={(owner) => set({ owner })} partners={can.partners} />
          </div>
        }
        columns={[
          {
            key: "user",
            label: "Driver",
            render: (d) => (
              <span className={styles.stack}>
                <TextButton onClick={() => setDialog({ kind: "details", driver: d })}>{d.user.name}</TextButton>
                <span className={styles.muted}>{formatPhone(d.user.phone_number) || "No phone"}</span>
              </span>
            ),
          },
          { key: "licence_number", label: "Licence", render: (d) => d.licence_number ?? "—" },
          {
            key: "licence_expires_at",
            label: "Licence expires",
            render: (d) =>
              d.licence_expires_at ? (
                <span className={d.licence_expires_at < today ? styles.danger : undefined}>
                  {formatDate(d.licence_expires_at, { ...format, timezone: "UTC" })}
                </span>
              ) : (
                "—"
              ),
          },
          {
            key: "highest_floor",
            label: "Floors",
            // decides FLOOR_TOO_HIGH when assigning; 3 when not recorded (as in backend-v2)
            render: (d) =>
              d.highest_floor === null ? <span className={styles.muted}>Up to 3</span> : `Up to ${d.highest_floor}`,
          },
          { key: "partner_id", label: "Owner", render: (d) => ownerName(d) },
          {
            key: "approval_status",
            label: "Approval",
            render: (d) => (
              <Badge tone={APPROVAL_STATUS[d.approval_status][1]}>{APPROVAL_STATUS[d.approval_status][0]}</Badge>
            ),
          },
          {
            key: "actions",
            label: "",
            render: (d) =>
              can.manage || can.wallets ? (
                <DriverMenu driver={d} can={can} onPick={(kind) => setDialog({ kind, driver: d })} />
              ) : null,
          },
        ]}
      />
      <p className={styles.hint}>
        New drivers start pending. Approve them so they can sign in to the driver app; they set their own PIN by SMS
        code.
      </p>
      {adding ? <DriverDialog canChooseOwner={can.partners} onClose={onAdded} /> : null}
      {dialog?.kind === "edit" ? (
        <DriverDialog driver={dialog.driver} canChooseOwner={can.partners} onClose={close} />
      ) : null}
      {dialog?.kind === "details" ? <DriverRecord driverId={dialog.driver.id} onClose={close} /> : null}
      {dialog?.kind === "approve" ? <ApproveDriver driver={dialog.driver} onClose={close} /> : null}
      {dialog?.kind === "suspend" ? <SuspendDriver driver={dialog.driver} onClose={close} /> : null}
      {dialog?.kind === "pin" ? <SetPinDialog driver={dialog.driver} onClose={close} /> : null}
      {dialog?.kind === "shift" ? (
        <OpenShiftDialog driver={{ id: dialog.driver.id, name: dialog.driver.user.name }} onClose={close} />
      ) : null}
    </>
  );
}

function DriverMenu({
  driver,
  can: role,
  onPick,
}: {
  driver: Driver;
  can: FleetCan;
  onPick: (kind: "edit" | "approve" | "suspend" | "shift" | "pin") => void;
}) {
  const navigate = useNavigate();
  const [noWallet, setNoWallet] = useState(false);
  const can = driverActionsFor(driver.approval_status, driver.status);
  const items: DropdownItem[] = [];
  if (role.manage) {
    if (can.approve) items.push({ label: "Approve", onSelect: () => onPick("approve") });
    if (can.openShift) items.push({ label: "Open shift", onSelect: () => onPick("shift") });
    items.push({ label: "Edit details", onSelect: () => onPick("edit") });
    // only a first PIN: one they have is theirs to change, or reset by SMS (P8-63)
    if (!driver.has_pin) items.push({ label: "Set PIN", onSelect: () => onPick("pin") });
    if (can.suspend) items.push({ label: "Suspend", destructive: true, onSelect: () => onPick("suspend") });
  }
  if (role.wallets)
    items.push({
      label: "Open wallet",
      separatorBefore: items.length > 0,
      onSelect: async () => {
        const wallet = await walletActions.of({ driver_id: driver.id });
        if (wallet) void navigate({ to: "/wallets/$walletId", params: { walletId: wallet.id } });
        else setNoWallet(true);
      },
    });
  if (role.payouts)
    items.push({
      label: "Payout requests",
      onSelect: () => void navigate({ to: "/payouts", search: { driver_id: driver.id, status: "all" } }),
    });
  return (
    <>
      <DropdownMenu label="Actions" items={items} />
      {noWallet && role.float ? (
        <FloatDialog driver={driver} onClose={() => setNoWallet(false)} />
      ) : noWallet ? (
        <ConfirmDialog
          title={`${driver.user.name} has no wallet yet`}
          description="It opens with their first money: a transfer from their partner, or a cash float from someone who manages wallets."
          action="OK"
          keep="Close"
          run={async () => {}}
          onClose={() => setNoWallet(false)}
        />
      ) : null}
    </>
  );
}

/**
 * A driver with no wallet yet: a cash float opens it (how far below zero they may spend, for
 * fuel and the like; never withdrawn). Their wallet's page opens after.
 */
function FloatDialog({ driver, onClose }: { driver: Driver; onClose: () => void }) {
  const navigate = useNavigate();
  const format = useCompanyFormat();
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const save = useMutation({
    mutationFn: () => walletActions.driverFloat(driver.id, amount.trim(), reason.trim()),
    onSuccess: (wallet) => void navigate({ to: "/wallets/$walletId", params: { walletId: wallet.id } }),
  });
  const check = useFormCheck();
  const errors = {
    // the API's rule for credit_limit
    amount: /^\d{1,12}(\.\d{1,2})?$/.test(amount.trim()) ? undefined : "Enter an amount, up to 2 decimals.",
    reason: reason.trim() ? undefined : "Enter a reason.",
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`${driver.user.name} has no wallet yet`}
        description="Give them a cash float to open it: how far below zero they may spend, for fuel and the like. They can never withdraw it."
      >
        <div className={styles.form}>
          <div className={styles.pair}>
            <Input
              label={`Cash float (${format.currency})`}
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="5000"
              error={check.show(errors.amount)}
            />
            <Input
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              description="Recorded."
              error={check.show(errors.reason)}
            />
          </div>
          {save.error ? (
            <Alert tone="danger" title="No float given">
              {messageFor(errorOf(save.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button loading={save.isPending} onClick={() => check.submit(errors, () => save.mutate())}>
              Give the float
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ApproveDriver({ driver, onClose }: { driver: Driver; onClose: () => void }) {
  const refresh = useRefreshFleet();
  return (
    <ConfirmDialog
      title={`Approve ${driver.user.name}?`}
      description="They can sign in to the driver app and be assigned deliveries. They set their PIN themselves with an SMS code."
      action="Approve"
      run={() => fleetActions.approveDriver(driver.id)}
      onDone={refresh}
      onClose={onClose}
    />
  );
}

function SuspendDriver({ driver, onClose }: { driver: Driver; onClose: () => void }) {
  const refresh = useRefreshFleet();
  return (
    <ConfirmDialog
      title={`Suspend ${driver.user.name}?`}
      description="They're signed out of the driver app and can't sign in again, and their open shift closes. Approve them again to undo it."
      action="Suspend"
      keep="Keep them"
      danger
      run={() => fleetActions.suspendDriver(driver.id)}
      onDone={refresh}
      onClose={onClose}
    />
  );
}

/**
 * Set the first PIN of a driver who has none (P8-63), for one who can't get the SMS code. Staff
 * type it and tell the driver; it's never shown again. The driver is signed out everywhere, any
 * lockout ends, and they're texted that the company set it.
 */
function SetPinDialog({ driver, onClose }: { driver: Driver; onClose: () => void }) {
  const refresh = useRefreshFleet();
  const [pin, setPin] = useState("");
  const valid = /^\d{4}$/.test(pin);
  const save = useMutation({
    mutationFn: () => fleetActions.setDriverPin(driver.id, pin),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`Set a PIN for ${driver.user.name}`}
        description="For a driver who can't get the SMS code. Tell them the PIN yourself: it isn't shown again. They can change it later in the app."
      >
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) save.mutate();
          }}
        >
          <PasswordField
            label="PIN"
            inputMode="numeric"
            autoComplete="off"
            maxLength={4}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            description="4 digits. They sign in to MajiTap Fleet with their phone number and this PIN."
          />
          <p className={styles.muted}>
            They're signed out wherever they're signed in, and get a text saying you set it (without the PIN).
          </p>
          {save.error ? (
            <Alert tone="danger" title="No PIN set">
              {messageFor(errorOf(save.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid} loading={save.isPending}>
              Set PIN
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Add a driver by phone number (the person is found or created; they start pending), or edit
 * their name, licence and ID. The phone isn't edited here: it's how they sign in. Staff who
 * manage partners also choose whose driver they are: a partner's, or the company's own.
 */
function DriverDialog({
  driver,
  canChooseOwner,
  onClose,
}: {
  driver?: Driver;
  canChooseOwner: boolean;
  onClose: () => void;
}) {
  const refresh = useRefreshFleet();
  const [phone, setPhone] = useState("");
  const [name, setName] = useState(driver?.user.name ?? "");
  const [licence, setLicence] = useState(driver?.licence_number ?? "");
  const [expires, setExpires] = useState<Date | undefined>(
    driver?.licence_expires_at ? asDay(driver.licence_expires_at) : undefined,
  );
  const [nationalId, setNationalId] = useState(driver?.national_id ?? "");
  const [floor, setFloor] = useState(driver?.highest_floor?.toString() ?? "");
  const [owner, setOwner] = useState<OwnerChoice>(driver?.partner_id ?? null);
  const check = useFormCheck();
  const errors = {
    // the API's rule for phone_number: E.164
    phone:
      driver || /^\+[1-9]\d{8,14}$/.test(phone.trim()) ? undefined : "Enter their phone number, e.g. +254712345678.",
    name: name.trim() ? undefined : "Enter their name.",
    // the API's range for highest_floor; number-ok: a floor number, not money
    floor: floor === "" || (/^\d{1,3}$/.test(floor) && Number(floor) <= 200) ? undefined : "A whole number, 0 to 200.",
  };
  const mutation = useMutation({
    mutationFn: () => {
      // a field emptied on an edit is removed (null); a new driver just leaves it out
      const blank = driver ? null : undefined;
      const details = {
        licence_number: licence.trim() || blank,
        licence_expires_at: expires ? pickerDay(expires) : blank,
        national_id: nationalId.trim() || blank,
        // number-ok: a floor number, not money
        highest_floor: floor ? Number.parseInt(floor, 10) : blank,
      } satisfies DriverDetails;
      const partner = canChooseOwner ? { partner_id: owner } : {};
      return driver
        ? fleetActions.updateDriver(driver.id, { ...details, ...partner, name: name.trim() })
        : fleetActions.createDriver({
            ...partner,
            phone_number: phone.trim(),
            name: name.trim(),
            licence_number: details.licence_number ?? undefined,
            licence_expires_at: details.licence_expires_at ?? undefined,
            national_id: details.national_id ?? undefined,
            highest_floor: details.highest_floor ?? undefined,
          });
    },
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={driver ? `Edit ${driver.user.name}` : "Add a driver"}
        description={driver ? undefined : "They start pending. Approve them when their papers check out."}
      >
        <div className={styles.form}>
          {driver ? null : (
            <Input
              label="Phone number"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+254712345678"
              description="They sign in to the driver app with it."
              error={check.show(errors.phone)}
            />
          )}
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            description={driver ? "Their own name: every company they work in sees it." : undefined}
            error={check.show(errors.name)}
          />
          <div className={styles.pair}>
            <Input label="Licence number (optional)" value={licence} onChange={(e) => setLicence(e.target.value)} />
            <DatePicker
              label="Licence expires (optional)"
              value={expires}
              onChange={setExpires}
              placeholder="Pick a date"
            />
          </div>
          <div className={styles.pair}>
            <Input label="National ID (optional)" value={nationalId} onChange={(e) => setNationalId(e.target.value)} />
            <Input
              label="Highest floor (optional)"
              inputMode="numeric"
              value={floor}
              onChange={(e) => setFloor(e.target.value)}
              description="The highest floor they carry water to. Empty means floor 3."
              error={check.show(errors.floor)}
            />
          </div>
          {canChooseOwner ? (
            <OwnerSelect
              value={owner}
              onChange={setOwner}
              description="A partner's driver drives its trucks, and the partner can pay them from its wallet."
            />
          ) : null}
          {mutation.error ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button loading={mutation.isPending} onClick={() => check.submit(errors, () => mutation.mutate())}>
              {driver ? "Save driver" : "Add driver"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** One driver (`GET /fleet/drivers/{id}`): everything recorded about them, fresh from the API. */
function DriverRecord({ driverId, onClose }: { driverId: string; onClose: () => void }) {
  const format = useCompanyFormat();
  const driver = useQuery({
    queryKey: ["fleet", "driver", driverId],
    queryFn: async () => {
      const { data, error } = await api.GET("/v1/admin/fleet/drivers/{id}", { params: { path: { id: driverId } } });
      if (error || !data) throw errorOf(error);
      return data;
    },
  });
  const d = driver.data;
  return (
    <RecordDrawer
      open
      onOpenChange={(o) => !o && onClose()}
      title={d?.user.name ?? "Driver"}
      description={d ? ownerName(d) : undefined}
    >
      {driver.status === "pending" ? (
        <PageLoading label="Loading the driver" />
      ) : driver.status === "error" ? (
        <PageError message={messageFor(errorOf(driver.error))} onRetry={() => driver.refetch()} />
      ) : d ? (
        <DetailList
          items={[
            { label: "Phone", value: formatPhone(d.user.phone_number) || null },
            { label: "Approval", value: APPROVAL_STATUS[d.approval_status][0] },
            { label: "PIN", value: d.has_pin ? "Set" : "Not set yet" },
            { label: "Owner", value: ownerName(d) },
            { label: "Licence", value: d.licence_number },
            {
              label: "Licence expires",
              value: d.licence_expires_at ? formatDate(d.licence_expires_at, { ...format, timezone: "UTC" }) : null,
            },
            { label: "National ID", value: d.national_id },
            {
              label: "Highest floor",
              value: d.highest_floor === null ? "Not recorded (up to 3)" : String(d.highest_floor),
            },
            { label: "Added", value: formatDateTime(d.created_at, format) },
          ]}
        />
      ) : null}
    </RecordDrawer>
  );
}
